import { AccountMonthlyBudget } from '../../account/types/AccountMonthlyBudget'
import { StatementDataType } from '../../financialStatements/types'
import { buildHistoryData, allocateBudget } from '../functions/allocationFunctions'
import { AllocationBudgetRow, AllocationMethod, MonthlyBudget } from '../types'

/**
 * Generic converter for MonthlyBudget[] to budget data with account/formula context.
 *
 * @param monthlyBudgets - Array of monthly budget allocations
 * @param idValue - Account code or formula ID
 * @param dimensionId - Dimension ID (null for account-level budgets)
 * @param dataType - Data type (budget, actuals, or forecast)
 * @param idKey - Property name for the ID ('accountCode' or 'formulaId')
 * @returns Array of budget data ready for backend API
 *
 * @example
 * ```typescript
 * // For account budgets
 * const accountBudgets = convertMonthlyBudgets(allocated, 3000, null, 'budget', 'accountCode')
 * // → [{ accountCode: 3000, year: 2025, month: 1, value: 1000, ... }]
 *
 * // For formula budgets
 * const formulaBudgets = convertMonthlyBudgets(allocated, 42, 'DIM1', 'budget', 'formulaId')
 * // → [{ formulaId: 42, year: 2025, month: 1, value: 1000, ... }]
 * ```
 */
export const convertMonthlyBudgets = <T extends { [key: string]: any }>(
  monthlyBudgets: MonthlyBudget[],
  idValue: number,
  dimensionId: string | null,
  dataType: StatementDataType,
  idKey: string
): T[] => {
  return monthlyBudgets.map(budget => ({
    [idKey]: idValue,
    dimensionId,
    dataType,
    ...budget
  })) as unknown as T[]
}

/**
 * Converts AllocationBudgetRow[] to budget data using provided converter
 *
 * Executes allocation for each row and combines results into a single array.
 * Uses flatMap to efficiently map and flatten in one operation.
 *
 * @param allocationRows - Rows with configured allocation methods
 * @param convertMonthlyBudget - Converter function for MonthlyBudget[] -> budget data array
 * @returns Array of monthly budgets ready for backend API
 *
 * @example
 * ```typescript
 * // Account budgets
 * const accountBudgets = applyAllocation(allocationRows, convertMonthlyBudgetsToAccountData)
 *
 * // Formula budgets
 * const formulaBudgets = applyAllocation(allocationRows, convertMonthlyBudgetsToFormulaData)
 * ```
 */
export const applyAllocation = <T = AccountMonthlyBudget>(
  allocationRows: AllocationBudgetRow[],
  convertMonthlyBudget: (monthlyBudgets: MonthlyBudget[], row: AllocationBudgetRow) => T[]
): T[] => {
  return allocationRows.flatMap(row => {
    let monthlyBudgets

    // If this is a deletion, generate null values for all periods
    if (row.isDeletion) {
      monthlyBudgets = row.targetPeriods.map(period => ({
        year: period.year,
        month: period.month,
        value: null
      }))
    } else {
      // Prepare allocation options with historyData for WEIGHTED method
      const { allocationOptions: rowOptions } = row
      let allocationOptions = { ...rowOptions }

      // Build historyData if WEIGHTED method and historyRange is provided
      if (row.allocationMethod === AllocationMethod.WEIGHTED && rowOptions?.historyRange) {
        const historyData = buildHistoryData(rowOptions.historyRange, row.actuals)
        allocationOptions = {
          ...allocationOptions,
          historyData
        }
      }

      // 1. Execute allocation function
      // NOTE: Values are stored with full precision; rounding happens only in UI formatting
      monthlyBudgets = allocateBudget(row.allocationMethod, row.value, row.targetPeriods, allocationOptions)
    }

    // 2. Convert to backend format using provided converter
    return convertMonthlyBudget(monthlyBudgets, row)
  })
}
